<template>
  <ehc-page>
    <ehc-header
      :text="`Organization: ${org.name}`"
      :actions="orgActions"
      @incrementTest="test()"
      @viewChange="orgEdit = { show: $event, data: { ...org } }"
      @recalcPropCount="recalcPropCount()"
      @showSubscriptionInfo="showSubscriptionInfo()"
      @grantGuestAsk="grantGuestAsk()"
      @grantElevatedEntitlements="openGrantDialog()"
      style="max-width: 500px"
    >
    </ehc-header>
    <ehc-sheet class="ml-2" width="500px">
      <div v-if="showDebug">
        OrgId: {{ org.orgId }}<br />
        Account:
        <pre>{{ org.account }}</pre>
        Subscription Expires: {{ org.account.expires }}<br />
        Subscription Type: {{ org.account.subscriptionType }}<br />
        Account Updated At: {{ org.account.updatedAt }}<br />
        Expires: {{ org.account_expires }}<br />
        {{ gcvOrgIsSubscribed ? " is subscribed" : " is not subscribed" }}<br />
        {{ gcvOrgIsPremium ? " is premium" : " is not premium" }}
        {{ gcvOrgIsGuestAsk ? " is Guest Ask" : " is not Guest Ask" }}
      </div>
      <ehc-form
        v-if="orgEdit.show"
        v-model="orgEdit.data"
        :meta="orgMetaEdit"
        dense
        @submit="updateOrg(orgEdit.data)"
      >
      </ehc-form>
      <ehc-show-obj
        v-model="org"
        :meta="orgMetaShow"
        @goToLink="goToLink($event)"
        @action="handleShowObjAction"
        v-else
      >
      </ehc-show-obj>
    </ehc-sheet>

    <ehc-entitlements-preview
      v-if="userHasPermission(['superAdmin'])"
      ref="entitlementsPreview"
      :org="org"
      :saving="entitlementsSaving"
      @save-overrides="saveFeatureOverrides"
      @save-grant="saveEntitlementGrant"
      @revoke-grant="revokeEntitlementGrant"
    ></ehc-entitlements-preview>

    <ehc-header
      v-if="userIsAdmin"
      text="Contact Email"
      style="max-width: 500px"
    ></ehc-header>
    <ehc-sheet v-if="userIsAdmin" class="ml-2" width="500px">
      <div class="text-caption mb-2">
        Organization email: {{ org.email || "(not set)" }}
      </div>
      <div class="text-caption mb-4">
        First member email: {{ firstMemberEmail || "(no members)" }}
      </div>
      <v-text-field
        v-model="newEmail"
        label="New Email"
        type="email"
        :rules="[emailRule]"
        :disabled="emailUpdateLoading"
        dense
        outlined
      ></v-text-field>
      <v-btn
        color="primary"
        :disabled="!canSaveEmail"
        :loading="emailUpdateLoading"
        @click="updateOrgAndUserEmail()"
      >
        Update Email
      </v-btn>
    </ehc-sheet>

    <!-- <ehc-organization-edit
      v-if="orgEdit.show"
      :value="orgEdit.data"
      @save="updateOrg($event)"
      @cancel="orgEdit = { show: false, data: {} }"
    ></ehc-organization-edit> -->

    <template v-if="hasWhiteLabelAccess || userIsAdmin">
      <ehc-header
        text="Branding"
        :actions="brandingActions"
        style="max-width: 500px"
        @viewChange="brandingEdit = { show: $event, data: { ...org } }"
      ></ehc-header>
      <ehc-sheet width="500px">
        <ehc-form
          v-model="brandingEdit.data"
          :meta="brandingMeta"
          v-if="brandingEdit.show"
          @submit="updateOrg(brandingEdit.data)"
          @removeImage="removeImage($event)"
          @onImageUpload="updateOrg({ [$event.key]: $event.fileName })"
        ></ehc-form>
        <ehc-show-obj v-model="org" :meta="brandingMeta" v-else></ehc-show-obj>
        <!--      WhiteLabel: {{org.companyLabel}}
              <br />
              Logo: 
              <v-img :src="org.companyImage" max-height="200" contain></v-img>
              <ehc-btn plain>change Logo</ehc-btn>
              <br />
              Org Background:
              <v-img :src="org.companyBackgroundImage" max-height="200" contain></v-img>-->
      </ehc-sheet>
    </template>

    <ehc-header
      text="Unanswered question emails"
      style="max-width: 500px"
    ></ehc-header>
    <ehc-sheet class="ml-2" width="500px">
      <p class="text-caption text--secondary mb-3">
        Default for when to email about guest questions Ask a question could
        not answer from the guidebook. Each property can override this in Guest
        Q&amp;A.
      </p>
      <v-select
        v-model="guestAiNotificationTiming"
        :items="guestAiNotificationOptions"
        item-text="label"
        item-value="value"
        label="When to email about unanswered guest questions"
        dense
        outlined
        :disabled="guestAiNotificationSaving"
      ></v-select>
      <v-btn
        color="primary"
        :loading="guestAiNotificationSaving"
        :disabled="!guestAiNotificationDirty"
        @click="saveGuestAiNotificationSettings()"
      >
        Save notification preference
      </v-btn>
    </ehc-sheet>

    <ehc-header text="Member Users"> </ehc-header>
    <ehc-sheet>
      <ehc-table
        :items="members"
        :headers="memberHeaders"
        :loading="membersLoading"
        @importProperties="importProperties($event)"
        @removeUser="removeUser($event)"
        @click:coll:uid="goToUserPage($event)"
      ></ehc-table>
    </ehc-sheet>

    <ehc-properties
      :key="propertiesKey"
      :orgObj="{ ...org, orgId: value }"
      @addProperty="getOrganization()"
      @deleteProperty="getOrganization()"
    ></ehc-properties>

    <v-btn
      v-if="userIsAdmin && org.propCount > 0"
      class="ma-3"
      text
      color="primary"
      @click="markOrgPropertiesDeleted(org.orgId)"
    >
      Delete ALL these properties
    </v-btn>

    <v-btn
      v-if="userIsAdmin && org.propCount === 0"
      class="ma-3"
      text
      color="error"
      @click="deleteOrganizationAndUsers(org.orgId)"
    >
      Delete Organization and Users
    </v-btn>

    <ehc-message v-model="message"></ehc-message>

    <v-dialog v-model="orgJsonDialogShow" max-width="800px" scrollable>
      <v-card>
        <v-card-title class="text-h5">
          Organization JSON
          <v-spacer></v-spacer>
          <v-btn icon @click="orgJsonDialogShow = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <pre class="org-json-pre">{{ orgJsonText }}</pre>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Subscription Information Dialog -->
    <ehc-subscription-dialog
      :show="subscriptionDialogShow"
      :orgId="value"
      :showDebug="showDebug"
      @close="subscriptionDialogShow = false"
    ></ehc-subscription-dialog>
  </ehc-page>
</template>

<script>
import ehcProperties from "@/components/properties/ehc-properties.vue";
import ehcSubscriptionDialog from "@/components/ehc-subscription-dialog.vue";
import ehcEntitlementsPreview from "@/components/ehc-entitlements-preview.vue";
import firebase from "firebase/app";
import "firebase/functions";
import "firebase/firestore";
import { ref } from "vue";

const { buildEntitlementGrant, hasFeature } = require("@/utils/entitlements.js");

export default {
  components: { ehcProperties, ehcSubscriptionDialog, ehcEntitlementsPreview },
  props: ["value"],
  data() {
    return {
      message: {
        show: false,
      },
      account: {},
      propertiesKey: 0,
      orgEdit: {
        show: false,
        data: {},
      },
      org: {
        membersData: [],
      },
      subscriptionDialogShow: false,
      orgJsonDialogShow: false,
      entitlementsSaving: false,
      // orgMeta is now a computed property
      orgMeta: [
        { type: "text", label: "Organization Name", key: "name" },
        { type: "link", label: "Web Site", key: "webSite" },
        { type: "intPhoneNumber", label: "Phone", key: "phone" },
        {
          type: "number",
          label: "Property Limit",
          key: "account_subscriptionCount",
        },
        {
          type: "select",
          label: "Subscription Type",
          key: "account_subscriptionType",
          options: ["Monthly", "Annual", "Annual10", "GuestAsk", "Pro", "Pro-10", "PRO10-90"],
          required: true,
          errorMessage: "you must select something",
        },
        {
          type: "date",
          label: "Subscription Expires",
          key: "account_expires",
        },
      ],
      brandingEdit: {
        show: false,
        data: {},
      },
      brandingMeta: [
        {
          type: "textArea",
          label: "Company White Label",
          key: "companyLabel",
          rows: 3,
          ["no-resize"]: true,
        },
        {
          type: "button",
          label: "Save Whitelabel",
          key: "submit",
          emitOnClick: "submit",
          color: "primary",
          dark: true,
          hideEhcShowObj: true,
        },
        { type: "divider", class: "mb-10 mt-6", hideEhcShowObj: true },
        {
          type: "imageUpload",
          label: "Company Logo",
          key: "companyImage",
          uploadPath: `companyPictures/background${Date.now()}`,
          confirmRemoveMessage:
            "Are you sure you want to remove this custom logo?",
          removeLabel: "Remove Logo",
          showInForm: true,
          showInEhcShowObj: true,
          ["max-width"]: "100",
          contain: true,
        },
        { type: "divider", class: "mb-10 mt-0", hideEhcShowObj: true },
        {
          type: "imageUpload",
          label: "Company Background",
          key: "companyBackgroundImage",
          uploadPath: `companyPictures/image${Date.now()}`,
          confirmRemoveMessage:
            "Are you sure you want to remove this custom background theme?",
          removeLabel: "Remove Background Theme",
          showInForm: true,
          showInEhcShowObj: true,
          ["max-width"]: "100",
          contain: true,
        },
      ],
      page: 1,
      membersLoading: false,
      members: [],
      showDebug: false,
      newEmail: "",
      emailUpdateLoading: false,
      guestAiNotificationTiming: "immediate",
      guestAiNotificationSaving: false,
      guestAiNotificationOptions: [
        {
          label: "Immediately — when a guest asks something the AI cannot answer",
          value: "immediate",
        },
        {
          label: "Next morning — one email early the next day (batched)",
          value: "next_morning",
        },
      ],
    };
  },
  computed: {
    hasWhiteLabelAccess() {
      return hasFeature(this.org, "whiteLabel");
    },
    guestAiNotificationDirty() {
      const saved =
        this.org?.guestAiNotifications?.timing === "next_morning"
          ? "next_morning"
          : "immediate";
      return this.guestAiNotificationTiming !== saved;
    },
    firstMemberEmail() {
      if (!this.org.members || this.org.members.length === 0) {
        return null;
      }
      const firstUid = this.org.members[0];
      const member = this.members.find((m) => m && m.uid === firstUid);
      return member ? member.email : null;
    },
    emailRule() {
      return (v) => /.+@.+\..+/.test(v) || "E-mail must be valid";
    },
    canSaveEmail() {
      if (this.emailUpdateLoading || !this.newEmail) {
        return false;
      }
      const normalized = this.newEmail.trim().toLowerCase();
      if (!/.+@.+\..+/.test(normalized)) {
        return false;
      }
      const current = (this.firstMemberEmail || this.org.email || "")
        .trim()
        .toLowerCase();
      return normalized !== current;
    },
    orgMetaComputed() {
      return [
        { type: "text", label: "Organization Name", key: "name" },
        { type: "link", label: "Web Site", key: "webSite" },
        { type: "intPhoneNumber", label: "Phone", key: "phone" },
        {
          type: "number",
          label: "Property Limit",
          key: "subscriptionCount",
        },
        {
          type: "select",
          label: "Subscription Type",
          key: "subscriptionType",
          options: ["Monthly", "Annual", "Annual10", "GuestAsk", "Pro", "Pro-10", "PRO10-90"],
          required: true,
          errorMessage: "you must select something",
        },
        {
          type: "date",
          label: "Subscription Expires",
          key: "subscriptionExpires",
        },
      ];
    },
    brandingActions() {
      let actions = [
        {
          actionType: "v-btn-toggle",
          dense: true,
          changeEmit: "viewChange",
          default: this.brandingEdit.show,
          mandatory: true,
          buttons: [
            { label: "View", value: false, outlined: true },
            { label: "Edit", value: true, outlined: true },
          ],
        },
      ];

      return actions;
    },
    memberHeaders() {
      let memberHeaders = [
        { text: "Display Name", value: "displayName" },
        ...this.userHasPermission(
          ["superAdmin"],
          [
            {
              type: "menu",
              icon: "mdi-dots-vertical",
              items: [
                {
                  type: "link",
                  label: "import users properties",
                  icon: "mdi-database-arrow-left-outline",
                  emitClick: "importProperties",
                  toolTip: "import users proerties into organization",
                },
                {
                  type: "link",
                  label: "remove user from organization",
                  color: "red",
                  icon: "mdi-trash-can-outline",
                  emitClick: "removeUser",
                  toolTip: "removes user from property",
                },
              ],
            },
          ],
          [],
        ),
        { text: "Email", value: "email" },
        { text: "User ID", value: "uid", clickable: true },
      ];
      return memberHeaders;
    },
    orgActions() {
      let actions = [
        ...this.userHasPermission(
          ["superAdmin"],
          [
            {
              actionType: "menu",
              color: "primary",
              items: [
                {
                  type: "link",
                  label: "recalculate property count",
                  icon: "mdi-database-refresh-outline",
                  emitClick: "recalcPropCount",
                  toolTip: "Recalculate property count",
                },
                {
                  type: "link",
                  label: "View Subscription Info",
                  icon: "mdi-credit-card-outline",
                  emitClick: "showSubscriptionInfo",
                  toolTip: "View detailed Stripe subscription information",
                },
                {
                  type: "link",
                  label: "Grant elevated features",
                  icon: "mdi-star-plus-outline",
                  emitClick: "grantElevatedEntitlements",
                  toolTip:
                    "Unlock a higher plan's features for a limited time without changing the Stripe subscription",
                },
                {
                  type: "link",
                  label: "Grant Guest Ask (beta)",
                  icon: "mdi-robot-outline",
                  emitClick: "grantGuestAsk",
                  toolTip:
                    "Unlock Guest Q&A and Import from listing for 1 year without changing Stripe",
                },
              ],
            },
          ],
          [],
        ),
        {
          actionType: "v-btn-toggle",
          dense: true,
          changeEmit: "viewChange",
          default: this.orgEdit.show,
          mandatory: true,
          buttons: [
            { label: "View", value: false, outlined: true },
            { label: "Edit", value: true, outlined: true },
          ],
        },
      ];

      return actions;
    },
    orgMetaShow() {
      return [
        ...this.orgMeta,
        { type: "number", label: "Property Count", key: "propCount" },
        {
          type: "number",
          label: "Published Property Count",
          key: "propCountPublished",
        },
        { type: "text", label: "Referred By", key: "referredBy" },
        {
          type: "action",
          label: "Organization JSON",
          linkText: "View",
          emitClick: "showOrgJson",
        },
      ];
    },
    orgJsonText() {
      try {
        const orgJson = this.omitAccountProperties(this.org);
        delete orgJson.searchIndex;
        return JSON.stringify(orgJson, null, 2);
      } catch (error) {
        return String(error);
      }
    },
    orgMetaEdit() {
      return [
        ...this.orgMeta,
        {
          type: "button",
          label: "submit",
          key: "submit",
          emitOnClick: "submit",
          color: "primary",
          dark: true,
          hideInCard: true,
        },
      ];
    },
  },
  watch: {
    org(val) {
      if (val.hasOwnProperty("membersData")) {
      }
    },
    value() {
      this.getOrganization();
    },
    async org() {
      await this.loadAccount();
    },
  },
  async created() {
    await this.loadPage();
    await this.loadAccount();
    this.showDebugToggle();
  },
  methods: {
    showSubscriptionInfo() {
      this.subscriptionDialogShow = true;
    },
    handleShowObjAction(item) {
      if (item && item.emitClick === "showOrgJson") {
        this.$nextTick(() => {
          this.orgJsonDialogShow = true;
        });
      }
    },
    async saveFeatureOverrides(overrides) {
      this.entitlementsSaving = true;
      const previous =
        (this.org.account && this.org.account.featureOverrides) || {};
      if (!this.org.account) {
        this.$set(this.org, "account", {});
      }
      this.$set(this.org.account, "featureOverrides", overrides);
      try {
        await this.apiUpdateDoc("organizations", this.value, {
          "account.featureOverrides": overrides,
        });
      } catch (error) {
        console.error("saveFeatureOverrides", error);
        this.$set(this.org.account, "featureOverrides", previous);
        this.message = {
          show: true,
          title: "Could not save feature overrides",
          message: error.message || "Please try again.",
        };
      } finally {
        this.entitlementsSaving = false;
      }
    },

    openGrantDialog() {
      if (this.$refs.entitlementsPreview) {
        this.$refs.entitlementsPreview.openGrantDialog();
      }
    },

    grantedByLabel() {
      return (
        this.$store.getters.user?.uid ||
        this.$store.getters.user?.email ||
        "unknown"
      );
    },

    async saveEntitlementGrant({ planKey, durationDays }) {
      const grant = buildEntitlementGrant({
        planKey,
        durationDays,
        grantedBy: this.grantedByLabel(),
      });
      if (!grant) {
        this.message = {
          show: true,
          title: "Grant failed",
          message: "Choose a valid elevated plan.",
          actions: [{ label: "ok", clickEmit: "cancel" }],
        };
        return;
      }
      await this.writeEntitlementGrant(grant);
    },

    async revokeEntitlementGrant() {
      const orgName = this.org?.name || this.value;
      const confirmed = confirm(
        `Revoke the elevated feature grant for ${orgName}? Features will return to the Stripe plan.`,
      );
      if (!confirmed) return;
      this.entitlementsSaving = true;
      const previous =
        (this.org.account && this.org.account.entitlementGrant) || null;
      if (!this.org.account) {
        this.$set(this.org, "account", {});
      }
      this.$set(this.org.account, "entitlementGrant", null);
      try {
        await this.apiUpdateDoc("organizations", this.value, {
          "account.entitlementGrant": firebase.firestore.FieldValue.delete(),
          "account.updatedAt": new Date().toISOString(),
        });
        await this.getOrganization();
      } catch (error) {
        console.error("revokeEntitlementGrant", error);
        this.$set(this.org.account, "entitlementGrant", previous);
        this.message = {
          show: true,
          title: "Could not revoke grant",
          message: error.message || "Please try again.",
          actions: [{ label: "ok", clickEmit: "cancel" }],
        };
      } finally {
        this.entitlementsSaving = false;
      }
    },

    async writeEntitlementGrant(grant) {
      const orgName = this.org?.name || this.value;
      this.entitlementsSaving = true;
      const previous =
        (this.org.account && this.org.account.entitlementGrant) || null;
      if (!this.org.account) {
        this.$set(this.org, "account", {});
      }
      this.$set(this.org.account, "entitlementGrant", grant);
      try {
        await this.apiUpdateDoc("organizations", this.value, {
          "account.entitlementGrant": grant,
          "account.updatedAt": new Date().toISOString(),
        });
        await this.getOrganization();
        const until = grant.expires
          ? new Date(grant.expires).toLocaleDateString()
          : "you revoke it";
        this.message = {
          show: true,
          title: "Elevated features granted",
          message: `${orgName} now has ${grant.planKey} features until ${until}. The Stripe subscription type is unchanged.`,
          width: "400",
          persistent: false,
          loading: false,
          actions: [{ label: "ok", clickEmit: "cancel" }],
        };
      } catch (error) {
        console.error("writeEntitlementGrant", error);
        this.$set(this.org.account, "entitlementGrant", previous);
        this.message = {
          show: true,
          title: "Grant failed",
          message: error.message || "Failed to save the entitlement grant.",
          width: "400",
          persistent: false,
          loading: false,
          actions: [{ label: "ok", clickEmit: "cancel" }],
        };
      } finally {
        this.entitlementsSaving = false;
      }
    },

    async grantGuestAsk() {
      const orgName = this.org?.name || this.value;
      const confirmed = confirm(
        `Grant Guest Ask to ${orgName} for 1 year? Unlocks Guest Q&A and Import from listing. The Stripe subscription type will not change.`,
      );
      if (!confirmed) return;
      const grant = buildEntitlementGrant({
        planKey: "GuestAsk",
        durationDays: 365,
        grantedBy: this.grantedByLabel(),
      });
      await this.writeEntitlementGrant(grant);
    },

    showDebugToggle() {
      if (this.$route?.query?.showDebug !== undefined) {
        this.showDebug = true;
      }
    },
    omitAccountProperties(originalObject) {
      const newObject = {};
      const prefix = "account_";

      // 1. Get all keys of the original object
      const keys = Object.keys(originalObject);

      // 2. Iterate over the keys
      keys.forEach((key) => {
        // 3. Check if the key starts with the specified prefix
        if (!key.startsWith(prefix)) {
          // 4. If it doesn't start with the prefix, copy the property to the new object
          // 5. Skip undefined values (Firebase doesn't accept undefined fields)
          if (originalObject[key] !== undefined) {
            newObject[key] = originalObject[key];
          }
        }
      });

      return newObject;
    },

    convertAccountToProps(data) {
      if (data && data.account && typeof data.account === "object") {
        Object.entries(data.account).forEach(([key, value]) => {
          data[`account_${key}`] = value;
        });
      }
      return data;
    },
    convertAccount_PropsToAccountObj(data) {
      let account = {};
      if (data) {
        Object.entries(data).forEach(([key, value]) => {
          if (key.startsWith("account_")) {
            let newKey = key.replace("account_", "");
            account[newKey] = value;
            delete data[key];
          }
        });
      }
      data.account = account;
      return data;
    },
    loadPage() {
      this.getOrganization().then((data) => {
        this.getListOfUsers(data.members);
      });
    },
    loadAccount() {
      this.account = this.org.account || {};
    },
    async removeUser(user) {
      this.apiRemoveUserFromOrg(this.value, user.uid).then(() => {
        this.loadPage();
      });
    },
    async importProperties(user) {
      this.message = {
        show: true,
        title: "importing Properties",
        message:
          "importing any properties belonging to user " +
          user.displayName +
          " - uid:  " +
          user.uid,
        width: "400",
        persistent: true,
        loading: true,
      };
      this.apiImportPropertiesFromUser(user.uid, this.value).then((resp) => {
        this.message = {
          show: true,
          title: "importing Properties",
          message: "imported " + resp + " properties.",
          width: "400",
          persistent: false,
          loading: false,
        };
      });
    },
    async recalcPropCount() {
      this.message = {
        show: true,
        title: "Recalculating on server",
        message:
          "Recounting active and published properties for organization " +
          this.value,
        width: "400",
        persistent: true,
        loading: true,
      };

      try {
        await this.apiRecalculatePropertyCounts(this.value);
        await this.getOrganization();
        this.message = { show: false };
      } catch (error) {
        console.error("recalcPropCount", error);
        this.message.title = "Recalculation failed";
        this.message.message =
          error.message || "Failed to recalculate property counts.";
        this.message.loading = false;
        this.message.persistent = false;
        this.message.actions = [{ label: "ok", clickEmit: "cancel" }];
      }
    },
    updateOrg: function (org) {
      this.convertAccount_PropsToAccountObj(org);
      org = this.omitAccountProperties(org);
      this.apiUpdateOrg(this.value, org).then((resp) => {
        this.getOrganization();
      });
      this.orgEdit = { show: false };
      this.brandingEdit = { show: false };
      /* this.showInfo = true
            this.showForm = false */
    },
    async saveGuestAiNotificationSettings() {
      this.guestAiNotificationSaving = true;
      try {
        await this.apiUpdateDoc("organizations", this.value, {
          guestAiNotifications: {
            timing: this.guestAiNotificationTiming,
          },
        });
        await this.getOrganization();
      } catch (error) {
        console.error("saveGuestAiNotificationSettings failed", error);
        this.message = {
          show: true,
          title: "Could not save notification preference",
          message: error.message || "Please try again.",
        };
      } finally {
        this.guestAiNotificationSaving = false;
      }
    },
    pageChange(page) {
      this.page = page;
    },
    getListOfUsers(list) {
      this.membersLoading = true;
      this.apiGetListOfUsers(list).then((data) => {
        this.members = data;
        this.membersLoading = false;
      });
    },
    async getOrganization() {
      try {
        let data = await this.apiGetOrg(this.value);
        // Map data.account.type to data.subType if available
        if (data.account?.status) {
          data.subType = data.account.status;
        }
        data = await this.convertAccountToProps(data);
        if (data.account && data.account.expires) {
          data.account_expires = data.account.expires;
        }
        if (!data.propertyLimit) {
          if (!data.subType || data.subType === "Free") {
            data.propertyLimit = this.gcvAppSettings.premiumPropertyLimit;
          }
          data.propertyLimit =
            data.subType === "Premium"
              ? this.gcvAppSettings.premiumPropertyLimit
              : undefined;
        }
        this.org = data;
        this.guestAiNotificationTiming =
          data?.guestAiNotifications?.timing === "next_morning"
            ? "next_morning"
            : "immediate";
        return data;
      } catch (error) {
        console.error("Error getting organization:", error);
        throw error;
      }
    },
    async onImageUpload(payload) {
      let key = payload.key;
      let obj = {};
      obj[key] = payload.fileName;
      await this.updateOrg(obj);
      this.orgEdit = false;
      this.org[payload.key] = payload.fileName;
    },
    goToLink(link) {
      if (link.includes("http")) {
        window.open(link, "_blank");
      }
    },
    goToUserPage(user) {
      if (user && user.uid) {
        this.$router.push({ name: "user", params: { id: user.uid } });
      }
    },
    async removeImage(payload) {
      let orgId = this.gcvOrgId;
      let obj = {};
      obj[payload.key] = null;
      await this.apiUpdateDoc("organizations", orgId, obj).then(() => {
        this.getOrganization();
      });
      this.orgEdit = { show: false };
      this.brandingEdit = { show: false };
    },

    async markOrgPropertiesDeleted(orgId) {
      const confirmed = confirm(
        "Are you sure you want to mark all properties in this organization as deleted?",
      );

      if (!confirmed) return;

      try {
        alert("Marking properties as deleted... Please wait.");

        const markOrgPropertiesDeleted = firebase
          .functions()
          .httpsCallable("markOrgPropertiesDeleted");
        const result = await markOrgPropertiesDeleted({ orgId: orgId });

        alert(
          `Successfully marked ${result.data.count} properties as deleted.`,
        );

        // Refresh the organization data and recalculate property count
        await this.recalcPropCount();
        await this.getOrganization();

        this.propertiesKey++;

        // Force ehc-properties to refresh by updating the orgObj prop
        this.$forceUpdate();
      } catch (error) {
        console.error("Error marking properties as deleted:", error);
        alert(
          "Failed to mark properties as deleted: " + (error.message || error),
        );
      }
    },

    async updateOrgAndUserEmail() {
      if (!this.canSaveEmail) {
        return;
      }

      this.emailUpdateLoading = true;
      this.message = {
        show: true,
        title: "Updating Email",
        message: "Updating organization and user email...",
        width: "400",
        persistent: true,
        loading: true,
      };

      try {
        const fn = firebase
          .functions()
          .httpsCallable("updateOrgAndUserEmail");
        const result = await fn({
          orgId: this.value,
          newEmail: this.newEmail.trim(),
        });

        this.newEmail = "";
        await this.loadPage();

        const data = result.data;
        this.message = {
          show: true,
          title: "Email Updated",
          message: data.unchanged
            ? "Email was already set to that value."
            : `Email updated to ${data.newEmail}.`,
          width: "400",
          persistent: false,
          loading: false,
        };
      } catch (error) {
        console.error("Error updating email:", error);
        this.message = {
          show: true,
          title: "Email Update Failed",
          message: error.message || "Failed to update email.",
          width: "400",
          persistent: false,
          loading: false,
        };
      } finally {
        this.emailUpdateLoading = false;
      }
    },

    async deleteOrganizationAndUsers(orgId) {
      // Double check property count
      if (this.org.propCount > 0) {
        alert(
          "Cannot delete organization. There are still properties listed. Please delete all properties first.",
        );
        return;
      }

      const confirmed = confirm(
        `Are you sure you want to DELETE this organization (${this.org.name}) and all its users? This action CANNOT be undone!`,
      );

      if (!confirmed) return;

      // Second confirmation
      const doubleConfirm = confirm(
        "This will permanently delete the organization and remove all associated users. Are you absolutely sure?",
      );

      if (!doubleConfirm) return;

      try {
        alert("Deleting organization and users... Please wait.");

        const deleteOrganizationAndUsers = firebase
          .functions()
          .httpsCallable("deleteOrganizationAndUsers");
        const result = await deleteOrganizationAndUsers({ orgId: orgId });

        alert(
          `Successfully deleted organization and ${
            result.data.deletedUsersCount || 0
          } users.`,
        );

        this.clearOrgsListCache();
        this.clearUsersListCache();
        this.$router.push("/organizations");
      } catch (error) {
        console.error("Error deleting organization:", error);
        alert("Failed to delete organization: " + (error.message || error));
      }
    },
  },
};
</script>

<style>
.org-json-pre {
  margin: 12px 0;
  padding: 12px;
  max-height: 70vh;
  overflow: auto;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  background: #f3f5f7;
  border-radius: 8px;
}
</style>
